<template>
  <div class="drivers-occupancy-suggested-leave px-4 pb-4">
    <div ref="occupancyToolbar" class="d-flex align-center py-2" style="gap: 8px;">
      <v-btn icon small @click="backToDriversList">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div>
        <div class="text-subtitle-1 font-weight-medium">
          Sugerowane kursy podstawienia
        </div>
        <div v-if="selectedDriverName" class="text-caption text--secondary">
          {{ selectedDriverName }}
        </div>
      </div>
      <v-spacer />
      <v-btn text color="primary" class="mb-1" @click="skipToSuggestedPickup">
        Pomiń
      </v-btn>
    </div>

    <div class="drivers-occupancy-suggested-leave__table-wrap">
      <v-data-table
        ref="suggestedLeaveTable"
        class="drivers-occupancy-table"
        :items="displayedTableItems"
        :headers="headers"
        item-key="rowKey"
        :items-per-page="-1"
        sort-by="confidence"
        :sort-desc="false"
        :custom-sort="sortSuggestedCoursesByConfidence"
        hide-default-footer
        fixed-header
        :height="tableHeight"
        no-data-text="Brak sugerowanych kursów podstawienia"
      >
        <template #item="{ item }">
          <DriversOccupancySuggestedCourseRow
            :item="item"
            :headers="headers"
            :row-actions="rowActions"
            :actions-offset="actionsOffset"
            actions-table-name="driversOccupancySuggestedLeave"
          />
        </template>
      </v-data-table>

      <div v-if="isSuggestedLeaveLoading" class="drivers-occupancy-suggested-leave__overlay">
        <v-progress-circular indeterminate color="primary" />
        <div class="mt-4 text-body-2 text--secondary">
          Trwa przygotowywanie sugerowanych kursów…
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import DriversOccupancySuggestedCourseRow from './DriversOccupancySuggestedCourseRow'
import tableActionsOffsetMixin from '../../mixins/tableActionsOffsetMixin'
import driversOccupancyTableHeightMixin from '../../mixins/driversOccupancyTableHeightMixin'
import { DRIVERS_OCCUPANCY_VIEW } from '../../store/driversOccupancy'
import { sortSuggestedCoursesByConfidence } from '../../utils/suggestedCourseConfidence'

// Bottom padding of the view, kept free below the table
const TABLE_HEIGHT_GUTTER = 0

const SUGGESTED_LEAVE_HEADERS = [
  { text: 'Nr zlecenia', value: 'orderRefNumber', sortable: false },
  { text: 'Typ kursu', value: 'courseType', sortable: false },
  { text: 'Klient', value: 'clientName', sortable: false, width: 200 },
  { text: 'Adres', value: 'addressDisplayName', sortable: false, width: 105 },
  { text: 'Kontener', value: 'container', sortable: false },
  { text: 'Pojemność', value: 'containerCapacity', sortable: false },
  { text: 'Kod odpadu', value: 'debrisTypeCode', sortable: false },
  { text: 'Nr BDO', value: 'bdoNumber', sortable: false },
  { text: 'Czas przyjazdu', value: 'arrivalTime', sortable: false },
  { text: 'Pewność', value: 'confidence', sortable: true },
  { text: 'Uzasadnienie', value: 'justification', sortable: false, width: 180 },
  { text: '', value: 'actions', sortable: false, width: 48 }
]

export default {
  components: {
    DriversOccupancySuggestedCourseRow
  },
  mixins: [tableActionsOffsetMixin, driversOccupancyTableHeightMixin],
  data: () => ({
    tableActionsRefName: 'suggestedLeaveTable',
    tableHeightGutter: TABLE_HEIGHT_GUTTER,
    headers: SUGGESTED_LEAVE_HEADERS,
    rowActions: []
  }),
  computed: {
    ...mapState({
      isOccupancyDialogOpen: state => state.driversOccupancy.isOccupancyDialogOpen,
      viewStep: state => state.driversOccupancy.viewStep,
      selectedDriver: state => state.driversOccupancy.selectedDriver,
      isSuggestedLeaveLoading: state => state.driversOccupancy.isSuggestedLeaveLoading,
      suggestedLeaveCourses: state => state.driversOccupancy.suggestedLeaveCourses
    }),
    selectedDriverName() {
      if (!this.selectedDriver) return ''
      return (
        this.selectedDriver.name ||
        `${this.selectedDriver.firstName || ''} ${this.selectedDriver.lastName || ''}`.trim()
      )
    },
    tableItems() {
      return (this.suggestedLeaveCourses || []).map((course, index) => ({
        ...course,
        rowKey: `${course.courseId}-${index}`
      }))
    },
    displayedTableItems() {
      return this.isSuggestedLeaveLoading ? [] : this.tableItems
    },
    tableActionsOffsetActive() {
      return (
        this.isOccupancyDialogOpen &&
        this.viewStep === DRIVERS_OCCUPANCY_VIEW.SUGGESTED_LEAVE &&
        !this.isSuggestedLeaveLoading
      )
    },
    tableActionsOffsetLayoutVersion() {
      return String(this.tableItems.length)
    }
  },
  created() {
    this.rowActions = [
      {
        name: 'selectSuggestedLeaveCourse',
        text: 'Wybierz kurs',
        icon: 'mdi-cursor-default-click',
        target: 'function',
        callback: item => this.onSuggestedLeaveCourseSelect(item)
      }
    ]
  },
  methods: {
    ...mapActions({
      backToDriversList: 'driversOccupancy/backToDriversList',
      skipToSuggestedPickup: 'driversOccupancy/skipToSuggestedPickup',
      selectSuggestedLeaveCourse: 'driversOccupancy/selectSuggestedLeaveCourse'
    }),
    sortSuggestedCoursesByConfidence,
    onSuggestedLeaveCourseSelect(course) {
      this.selectSuggestedLeaveCourse(course)
    }
  }
}
</script>

<style lang="scss" scoped>
.drivers-occupancy-suggested-leave {
  display: flex;
  flex-direction: column;
  min-height: 160px;
  height: 100%;

  &__table-wrap {
    position: relative;
    flex: 1;
    min-height: 160px;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.82);
    pointer-events: all;
  }
}
</style>
